import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CloudOff, RefreshCw } from "lucide-react";
import { flushQueue, pendingCount } from "@/lib/offlineQueue";

export function OfflineSync() {
  const [pending, setPending] = useState(pendingCount());
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);

  const sync = useCallback(async () => {
    if (!navigator.onLine || pendingCount() === 0) return;
    setSyncing(true);
    const { synced } = await flushQueue();
    setSyncing(false);
    setPending(pendingCount());
    if (synced > 0) {
      toast.success(`${synced} offline pre-start${synced > 1 ? "s" : ""} synced`);
    }
  }, []);

  useEffect(() => {
    const refresh = () => setPending(pendingCount());
    const onOnline = () => { setOnline(true); sync(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("prestart-queue-changed", refresh);
    sync(); // attempt on load
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("prestart-queue-changed", refresh);
    };
  }, [sync]);

  if (pending === 0) return null;

  return (
    <div className="fixed bottom-3 left-3 z-[90]" data-testid="offline-sync-pill">
      <div className="flex items-center gap-2 bg-[#1A1C23] border border-[#FFB800]/40 rounded-full px-3.5 py-2 shadow-lg shadow-black/50">
        {online ? (
          <RefreshCw className={`w-4 h-4 text-[#FFB800] ${syncing ? "animate-spin" : ""}`} />
        ) : (
          <CloudOff className="w-4 h-4 text-[#94A3B8]" />
        )}
        <span className="font-mono text-xs text-white">
          {pending} pre-start{pending > 1 ? "s" : ""} {online ? (syncing ? "syncing…" : "to sync") : "saved offline"}
        </span>
        {online && !syncing && (
          <button onClick={sync} className="font-head text-[10px] uppercase tracking-widest text-[#FFB800] ml-1" data-testid="offline-sync-now">
            Sync
          </button>
        )}
      </div>
    </div>
  );
}
